import { useState, useEffect } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '@/lib/supabaseClient';
import { toast } from 'sonner';
import {
  Save, Users, ShieldCheck, CheckCircle,
  Building2, Loader2, BarChart3
} from 'lucide-react';

const SocialMetricsPage = () => {
  const navigate = useNavigate();
  const { currentOrg, user, userData } = useOutletContext();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    // Employee Diversity
    total_employees: '',
    employees_male: '',
    employees_female: '',
    employees_non_binary: '',
    employees_not_disclosed: '',

    // Leadership Diversity
    leadership_total: '',
    leadership_male: '',
    leadership_female: '',
    leadership_non_binary: '',

    // Modern Slavery
    modern_slavery_statement_published: false,
    modern_slavery_last_reviewed: '',
    modern_slavery_supplier_audits: false,
    modern_slavery_training_provided: false,
    modern_slavery_grievance_mechanism: false,

    // Reporting Period
    reporting_period_end: ''
  });

  useEffect(() => {
    if (currentOrg) {
      fetchSocialData();
    }
  }, [currentOrg?.id]);

  const fetchSocialData = async () => {
    if (!currentOrg) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      // Try to fetch existing social metrics data
      const { data: metricsData } = await supabase
        .from('social_metrics')
        .select('*')
        .eq('organisation_id', currentOrg.id)
        .maybeSingle();

      if (metricsData) {
        setFormData(prev => ({
          ...prev,
          total_employees: metricsData.total_employees || '',
          employees_male: metricsData.employees_male || '',
          employees_female: metricsData.employees_female || '',
          employees_non_binary: metricsData.employees_non_binary || '',
          employees_not_disclosed: metricsData.employees_not_disclosed || '',
          leadership_total: metricsData.leadership_total || '',
          leadership_male: metricsData.leadership_male || '',
          leadership_female: metricsData.leadership_female || '',
          leadership_non_binary: metricsData.leadership_non_binary || '',
          modern_slavery_statement_published: metricsData.modern_slavery_statement_published || false,
          modern_slavery_last_reviewed: metricsData.modern_slavery_last_reviewed ? metricsData.modern_slavery_last_reviewed.split('T')[0] : '',
          modern_slavery_supplier_audits: metricsData.modern_slavery_supplier_audits || false,
          modern_slavery_training_provided: metricsData.modern_slavery_training_provided || false,
          modern_slavery_grievance_mechanism: metricsData.modern_slavery_grievance_mechanism || false,
          reporting_period_end: metricsData.reporting_period_end ? metricsData.reporting_period_end.split('T')[0] : ''
        }));
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load social metrics data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentOrg) return;

    try {
      setSaving(true);

      // Prepare data for upsert
      const metricsData = {
        organisation_id: currentOrg.id,
        total_employees: formData.total_employees ? parseInt(formData.total_employees) : null,
        employees_male: formData.employees_male ? parseInt(formData.employees_male) : null,
        employees_female: formData.employees_female ? parseInt(formData.employees_female) : null,
        employees_non_binary: formData.employees_non_binary ? parseInt(formData.employees_non_binary) : null,
        employees_not_disclosed: formData.employees_not_disclosed ? parseInt(formData.employees_not_disclosed) : null,
        leadership_total: formData.leadership_total ? parseInt(formData.leadership_total) : null,
        leadership_male: formData.leadership_male ? parseInt(formData.leadership_male) : null,
        leadership_female: formData.leadership_female ? parseInt(formData.leadership_female) : null,
        leadership_non_binary: formData.leadership_non_binary ? parseInt(formData.leadership_non_binary) : null,
        modern_slavery_statement_published: formData.modern_slavery_statement_published,
        modern_slavery_last_reviewed: formData.modern_slavery_last_reviewed || null,
        modern_slavery_supplier_audits: formData.modern_slavery_supplier_audits,
        modern_slavery_training_provided: formData.modern_slavery_training_provided,
        modern_slavery_grievance_mechanism: formData.modern_slavery_grievance_mechanism,
        reporting_period_end: formData.reporting_period_end || null
      };

      // Upsert - update if exists, insert if not
      const { error } = await supabase
        .from('social_metrics')
        .upsert(metricsData, { onConflict: 'organisation_id' });

      if (error) {
        console.error('Error saving:', error);
        toast.error('Failed to save social metrics');
        return;
      }

      toast.success('Social metrics saved successfully');
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Failed to save social metrics');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Calculate percentages
  const totalEmployees = parseInt(formData.total_employees) || 0;
  const malePercent = totalEmployees > 0 ? ((parseInt(formData.employees_male) || 0) / totalEmployees * 100).toFixed(1) : 0;
  const femalePercent = totalEmployees > 0 ? ((parseInt(formData.employees_female) || 0) / totalEmployees * 100).toFixed(1) : 0;
  const nonBinaryPercent = totalEmployees > 0 ? ((parseInt(formData.employees_non_binary) || 0) / totalEmployees * 100).toFixed(1) : 0;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA]">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#003B5C] mx-auto mb-4" />
          <p className="text-slate-600">Loading social metrics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#F8F9FA] min-h-[calc(100vh-56px)]" data-testid="social-metrics-page">
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Page Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#003B5C] flex items-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <Users className="w-7 h-7 mr-3 text-[#74A57F]" />
              Social Metrics
            </h1>
            <p className="text-slate-600 mt-1">
              {currentOrg?.name} • Track diversity and modern slavery compliance
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Employee Diversity Section */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-[#003B5C]">Employee Diversity</h2>
                <p className="text-sm text-slate-500">Gender breakdown of your workforce</p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <Label htmlFor="total_employees">Total Employees</Label>
                <Input
                  id="total_employees"
                  type="number"
                  value={formData.total_employees}
                  onChange={(e) => updateField('total_employees', e.target.value)}
                  placeholder="e.g., 50"
                  className="mt-1 w-48"
                  data-testid="total-employees-input"
                />
              </div>

              <div>
                <Label htmlFor="employees_male">Male</Label>
                <div className="flex items-center space-x-3 mt-1">
                  <Input
                    id="employees_male"
                    type="number"
                    value={formData.employees_male}
                    onChange={(e) => updateField('employees_male', e.target.value)}
                    placeholder="0"
                    className="w-24"
                    data-testid="employees-male-input"
                  />
                  {totalEmployees > 0 && (
                    <span className="text-sm text-slate-500">{malePercent}%</span>
                  )}
                </div>
              </div>

              <div>
                <Label htmlFor="employees_female">Female</Label>
                <div className="flex items-center space-x-3 mt-1">
                  <Input
                    id="employees_female"
                    type="number"
                    value={formData.employees_female}
                    onChange={(e) => updateField('employees_female', e.target.value)}
                    placeholder="0"
                    className="w-24"
                    data-testid="employees-female-input"
                  />
                  {totalEmployees > 0 && (
                    <span className="text-sm text-slate-500">{femalePercent}%</span>
                  )}
                </div>
              </div>

              <div>
                <Label htmlFor="employees_non_binary">Non-Binary / Other</Label>
                <div className="flex items-center space-x-3 mt-1">
                  <Input
                    id="employees_non_binary"
                    type="number"
                    value={formData.employees_non_binary}
                    onChange={(e) => updateField('employees_non_binary', e.target.value)}
                    placeholder="0"
                    className="w-24"
                    data-testid="employees-nonbinary-input"
                  />
                  {totalEmployees > 0 && (
                    <span className="text-sm text-slate-500">{nonBinaryPercent}%</span>
                  )}
                </div>
              </div>

              <div>
                <Label htmlFor="employees_not_disclosed">Prefer Not to Say</Label>
                <Input
                  id="employees_not_disclosed"
                  type="number"
                  value={formData.employees_not_disclosed}
                  onChange={(e) => updateField('employees_not_disclosed', e.target.value)}
                  placeholder="0"
                  className="mt-1 w-24"
                  data-testid="employees-notdisclosed-input"
                />
              </div>
            </div>

            {/* Visual breakdown */}
            {totalEmployees > 0 && (
              <div className="mt-6 pt-6 border-t border-slate-100">
                <Label className="text-sm text-slate-500 mb-3 block">Gender Distribution</Label>
                <div className="h-4 rounded-full overflow-hidden flex bg-slate-100">
                  {parseFloat(malePercent) > 0 && (
                    <div
                      className="bg-blue-500 h-full"
                      style={{ width: `${malePercent}%` }}
                      title={`Male: ${malePercent}%`}
                    />
                  )}
                  {parseFloat(femalePercent) > 0 && (
                    <div
                      className="bg-pink-500 h-full"
                      style={{ width: `${femalePercent}%` }}
                      title={`Female: ${femalePercent}%`}
                    />
                  )}
                  {parseFloat(nonBinaryPercent) > 0 && (
                    <div
                      className="bg-purple-500 h-full"
                      style={{ width: `${nonBinaryPercent}%` }}
                      title={`Non-Binary: ${nonBinaryPercent}%`}
                    />
                  )}
                </div>
                <div className="flex items-center space-x-4 mt-2 text-xs">
                  <span className="flex items-center"><span className="w-3 h-3 bg-blue-500 rounded mr-1"></span> Male</span>
                  <span className="flex items-center"><span className="w-3 h-3 bg-pink-500 rounded mr-1"></span> Female</span>
                  <span className="flex items-center"><span className="w-3 h-3 bg-purple-500 rounded mr-1"></span> Non-Binary</span>
                </div>
              </div>
            )}
          </div>

          {/* Leadership Diversity Section */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-[#003B5C]">Leadership Diversity</h2>
                <p className="text-sm text-slate-500">Gender breakdown of senior leadership / board</p>
              </div>
            </div>

            <div className="grid md:grid-cols-4 gap-4">
              <div>
                <Label htmlFor="leadership_total">Total Leadership</Label>
                <Input
                  id="leadership_total"
                  type="number"
                  value={formData.leadership_total}
                  onChange={(e) => updateField('leadership_total', e.target.value)}
                  placeholder="0"
                  className="mt-1"
                  data-testid="leadership-total-input"
                />
              </div>
              <div>
                <Label htmlFor="leadership_male">Male</Label>
                <Input
                  id="leadership_male"
                  type="number"
                  value={formData.leadership_male}
                  onChange={(e) => updateField('leadership_male', e.target.value)}
                  placeholder="0"
                  className="mt-1"
                  data-testid="leadership-male-input"
                />
              </div>
              <div>
                <Label htmlFor="leadership_female">Female</Label>
                <Input
                  id="leadership_female"
                  type="number"
                  value={formData.leadership_female}
                  onChange={(e) => updateField('leadership_female', e.target.value)}
                  placeholder="0"
                  className="mt-1"
                  data-testid="leadership-female-input"
                />
              </div>
              <div>
                <Label htmlFor="leadership_non_binary">Non-Binary</Label>
                <Input
                  id="leadership_non_binary"
                  type="number"
                  value={formData.leadership_non_binary}
                  onChange={(e) => updateField('leadership_non_binary', e.target.value)}
                  placeholder="0"
                  className="mt-1"
                  data-testid="leadership-nonbinary-input"
                />
              </div>
            </div>
          </div>

          {/* Modern Slavery Section */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <ShieldCheck className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-[#003B5C]">Modern Slavery Verification</h2>
                <p className="text-sm text-slate-500">Compliance with Modern Slavery Act 2018</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <Checkbox
                  id="modern_slavery_statement"
                  checked={formData.modern_slavery_statement_published}
                  onCheckedChange={(checked) => updateField('modern_slavery_statement_published', checked)}
                  data-testid="modern-slavery-statement-checkbox"
                />
                <div>
                  <Label htmlFor="modern_slavery_statement" className="text-sm font-medium cursor-pointer">
                    Modern Slavery Statement Published
                  </Label>
                  <p className="text-xs text-slate-500">
                    We have published a Modern Slavery Statement on our website (required for entities with $100M+ revenue)
                  </p>
                </div>
              </div>

              {formData.modern_slavery_statement_published && (
                <div className="ml-7">
                  <Label htmlFor="modern_slavery_last_reviewed">Statement Last Reviewed</Label>
                  <Input
                    id="modern_slavery_last_reviewed"
                    type="date"
                    value={formData.modern_slavery_last_reviewed}
                    onChange={(e) => updateField('modern_slavery_last_reviewed', e.target.value)}
                    className="mt-1 w-48"
                    data-testid="modern-slavery-reviewed-input"
                  />
                </div>
              )}

              <div className="flex items-start space-x-3">
                <Checkbox
                  id="supplier_audits"
                  checked={formData.modern_slavery_supplier_audits}
                  onCheckedChange={(checked) => updateField('modern_slavery_supplier_audits', checked)}
                  data-testid="supplier-audits-checkbox"
                />
                <div>
                  <Label htmlFor="supplier_audits" className="text-sm font-medium cursor-pointer">
                    Supplier Due Diligence Conducted
                  </Label>
                  <p className="text-xs text-slate-500">
                    We conduct audits or assessments of our value chain for modern slavery risks
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <Checkbox
                  id="training_provided"
                  checked={formData.modern_slavery_training_provided}
                  onCheckedChange={(checked) => updateField('modern_slavery_training_provided', checked)}
                  data-testid="training-provided-checkbox"
                />
                <div>
                  <Label htmlFor="training_provided" className="text-sm font-medium cursor-pointer">
                    Staff Training Provided
                  </Label>
                  <p className="text-xs text-slate-500">
                    Relevant staff receive training on identifying and reporting modern slavery risks
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <Checkbox
                  id="grievance_mechanism"
                  checked={formData.modern_slavery_grievance_mechanism}
                  onCheckedChange={(checked) => updateField('modern_slavery_grievance_mechanism', checked)}
                  data-testid="grievance-mechanism-checkbox"
                />
                <div>
                  <Label htmlFor="grievance_mechanism" className="text-sm font-medium cursor-pointer">
                    Grievance Mechanism in Place
                  </Label>
                  <p className="text-xs text-slate-500">
                    We have a mechanism for workers and others to report concerns about modern slavery
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Reporting Period */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="reporting_period">Reporting Period End Date</Label>
                <p className="text-xs text-slate-500 mt-1">The date these metrics apply to</p>
              </div>
              <Input
                id="reporting_period"
                type="date"
                value={formData.reporting_period_end}
                onChange={(e) => updateField('reporting_period_end', e.target.value)}
                className="w-48"
                data-testid="reporting-period-input"
              />
            </div>
          </div>

          {/* Info Box */}
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-start space-x-3">
            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-green-800 font-medium">Why track social metrics?</p>
              <p className="text-sm text-green-700 mt-1">
                Social metrics demonstrate your commitment to diversity and ethical value chains.
                Many grant programs and tender submissions now require evidence of ESG performance beyond just environmental factors.
              </p>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end space-x-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/dashboard')}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="bg-[#003B5C] hover:bg-[#002A42] text-white"
              data-testid="save-social-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Social Metrics
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SocialMetricsPage;
